/**
 * Engine-agnostic query execution types shared between renderer and main process.
 * These types define a result model that does not depend on SQL Tools Service or any
 * engine-specific concepts like ownerUri.
 */

import type { ExecutionPlanArtifact } from './executionPlan';
import type { JsonClassification } from '@/features/sql-resolver/types/schema';

/**
 * Request to execute a query - engine-neutral
 */
export type QueryExecutionRequest = {
  /** Connection ID to execute the query on */
  connectionId: string;
  /** SQL query text */
  sql: string;
  /** Optional database name to use */
  database?: string;
  /** Unique execution ID for correlation and cancellation (renderer-generated) */
  executionId: string;
  /** Optional execution options */
  options?: QueryExecutionOptions;
  /** Tab ID - used to route through transaction session when active */
  tabId?: string;
};

export type QueryExecutionOptions = {
  /** Maximum rows to return per result set */
  maxRows?: number;
  /** Approximate result-size cap in bytes (advisory; enforced post-hoc by callers) */
  maxBytes?: number;
  /** Timeout in milliseconds */
  timeoutMs?: number;
  /**
   * Internal: skip source-table provenance / key enrichment for this execution.
   * Set by Table Explorer's count and estimate lanes — their single-row results
   * never carried provenance, and the dictionary round trips only widened the
   * cancel window. Honoured by the Oracle adapter; PG, MySQL and the MSSQL
   * driver adapter follow in the cross-engine parity task.
   */
  skipProvenance?: boolean;
  /** Optional execution plan capture */
  plan?: { mode: 'estimated' | 'actual' };
  /**
   * Driver-typed parameters (S10 dashboards). When present, the SQL contains
   * engine-native placeholders emitted by the dashboard compose step (mssql
   * `@pN`, pgsql `$n`, oracle `:pN`, mysql/sqlite `?`) in `parameters` order,
   * and the adapter MUST execute the text as a SINGLE parameterized statement
   * (no multi-statement splitting). Values are serialized as strings; `type`
   * tells the adapter how to coerce before binding.
   */
  parameters?: QueryDriverParameter[];
  /**
   * The caller can consume windowed results (`resultSetCacheUris` +
   * `result-source:progress` events). Only the Query Editor sets this. When
   * absent, the MSSQL SSTS adapter fetches rows inline instead of leaving
   * them server-side in STS windowed mode — services that read
   * `resultSets[].rows` directly (database/object listings, peeks, probes)
   * would otherwise see zero rows on SSTS-routed connections.
   */
  streamResults?: boolean;
  /**
   * The renderer believes this tab has an ACTIVE transaction (manual / smart
   * mode after BEGIN). When set and the backend holds no transaction session
   * for the tab — a Reconnect or disconnect ended it — the statement is
   * REFUSED with `error.code === 'TRANSACTION_SESSION_MISSING'` instead of
   * silently running in auto-commit.
   */
  transactionSessionExpected?: boolean;
  /**
   * The session the renderer BEGAN (`transaction:begin` → `sessionGeneration`).
   * A statement is refused when the tab's current session is a different one —
   * a teardown plus a new begin landed in between — rather than run on the
   * replacement.
   */
  transactionSessionGeneration?: number;
};

export type QueryDriverParameter = {
  name: string;
  type: 'string' | 'number' | 'date' | 'boolean';
  value: string;
};

/**
 * Result of a query execution - engine-neutral
 */
export type QueryExecutionResult = {
  /** Whether the query completed successfully */
  success: boolean;
  /** Result sets returned by the query (may be multiple for batch queries) */
  resultSets?: QueryResultSet[];
  /** Messages/warnings/info from the execution */
  messages?: QueryExecutionMessage[];
  /** Execution statistics */
  stats?: QueryExecutionStats;
  /** Optional execution plan reference (payload stored in main process) */
  plan?: ExecutionPlanArtifact;
  /** Error if success is false */
  error?: QueryExecutionError;
  /** True if the query was cancelled by user */
  cancelled?: boolean;
  /**
   * Per-result-set cache URIs in Electron main process.
   * When present, rows in resultSets are empty — fetch via result-cache:get-window.
   * Index corresponds to resultSets index. Slots may be `undefined` for
   * result sets that have no cache (e.g., DML/DDL affected-rows responses).
   */
  resultSetCacheUris?: (string | undefined)[];
  /**
   * STS-backed result set metadata (MSSQL only).
   * When present, executeQueryWithCache creates STS sources instead of TempSqlite.
   * The adapter skips row fetching — STS keeps data server-side.
   */
  stsInfo?: StsResultSetInfo;
  /**
   * Item 5 — which MSSQL engine adapter handled this execution. Set by
   * `mssqlRoutingAdapter` after delegating to `mssqlDriverAdapter` or
   * `mssqlSqlToolsAdapter`. Absent for non-MSSQL engines and for results
   * that didn't go through the routing adapter (e.g. direct calls from the
   * diagnostics battery, backup/restore, clone-database). Two-value
   * low-cardinality enum — fully PII-safe.
   */
  executionAdapter?: 'driver' | 'sqltools';
};

export type StsResultSetInfo = {
  ownerUri: string;
  resultSets: Array<{
    batchIndex: number;
    resultSetIndex: number;
    rowCount: number;
    columns: QueryColumn[];
  }>;
};

/**
 * A raw row read from a SQL Tools Service `query/subset` window. Cells are
 * `displayValue` strings EXCEPT for SQL NULL, which the MSSQL SqlTools adapter
 * maps to real `null` — STS renders NULL as the sentinel STRING "NULL", which
 * must never reach the grid (it would be indistinguishable from a column whose
 * actual value is the text 'NULL').
 */
export type StsRawRow = (string | null)[];

/**
 * A single result set from a query
 */
export type QueryResultSet = {
  /** Column definitions */
  columns: QueryColumn[];
  /** Row data as objects keyed by column id (fallback: column name) */
  rows: Record<string, unknown>[];
  /** Total row count (may differ from rows.length if truncated) */
  rowCount?: number;
  /** Per-resultSet error for batch queries where some statements succeed */
  error?: QueryExecutionError;
  /** Kusto `| render` directive from the response's QueryProperties frame (kusto engine only). */
  renderHint?: { visualization: string; title?: string; xcolumn?: string; ycolumns?: string[] };
};

/**
 * Column metadata
 */
export type QueryColumn = {
  /**
   * Stable unique column id used as the key in `QueryResultSet.rows`.
   * Required when duplicate column names exist (e.g. `SELECT *` with JOINs).
   * When omitted, consumers should fall back to `name`.
   */
  id?: string;
  /** Column name */
  name: string;
  /** Data type (engine-specific, for display purposes) */
  dataType?: string;
  /** Whether the column allows NULL */
  nullable?: boolean;
  /** Whether this column is part of a primary key (when determinable) */
  isPrimaryKey?: boolean;
  /** Whether this column is part of a foreign key (when determinable) */
  isForeignKey?: boolean;
  /** Source schema of the column (e.g. 'dbo', 'public') — for edit provenance */
  sourceSchema?: string;
  /** Source table of the column (e.g. 'users') — for edit provenance */
  sourceTable?: string;
  /** Original column name in source table (may differ from alias) — for edit provenance */
  sourceColumn?: string;
  /** Primary key columns for the source table (original column names) */
  sourceTablePrimaryKeyColumns?: string[];
  /** True for computed/expression columns with no source table */
  isComputed?: boolean;
  /**
   * JSON column classification lattice (Ralph/JsonColumnSemantics/PRD.md § A1).
   * When set with `kind !== 'not-json'`, the Result Grid auto-formats cells
   * as JSON. When set to `{ kind: 'not-json' }`, JSON-shaped cell values are
   * tagged with a "Looks like JSON" tooltip but not auto-formatted (E1
   * value-shape fallback). When undefined, the grid falls back to a sampled
   * row heuristic.
   */
  jsonClassification?: JsonClassification;
  /**
   * True when the column holds LOB / unbounded-size values (e.g. nvarchar(max), text) —
   * reported by SQL Tools Service; other engines may leave it unset.
   */
  isLong?: boolean;
};

/**
 * A message from query execution (info, warning, or error)
 */
export type QueryExecutionMessage = {
  /** Message type */
  type: 'info' | 'warning' | 'error';
  /** Message text */
  text: string;
};

/**
 * Query execution statistics
 */
export type QueryExecutionStats = {
  /** Execution time in milliseconds */
  executionTimeMs?: number;
  /** Total rows affected/returned */
  totalRowCount?: number;
  /** Number of rows actually returned (may be less if truncated) */
  returnedRowCount?: number;
  /** Whether results were truncated due to maxRows limit */
  truncated?: boolean;
  /**
   * Rows changed by the run's data-modifying statements (INSERT / UPDATE /
   * DELETE / MERGE …), summed over every statement and GO batch — the total
   * of the per-statement `N row(s) affected` lines, minus SQL Server's counts
   * for statements that returned a result set. `undefined` when no statement
   * reported such a count (SELECT-only, DDL, `SET NOCOUNT ON`, Kusto), so 0
   * still means "ran, changed nothing". `totalRowCount` / `returnedRowCount`
   * keep their per-engine meaning; the Query Editor derives its wording from
   * this field (`formatExecutionRowSummary`).
   */
  affectedRowCount?: number;
  /**
   * Main-process time from execution start to the first streamed batch (ms).
   * The first batch may be a columns-only shell (zero rows) — this measures
   * the moment the renderer first receives something to paint (headers /
   * spinner drop), not necessarily the first data row.
   */
  timeToFirstBatchMs?: number;
};

/**
 * Error information
 */
export type QueryExecutionError = {
  /** Error message */
  message: string;
  /** Optional error code (engine-specific, string form — e.g. 'ETIMEOUT', 'ECONNRESET') */
  code?: string;
  /**
   * With `code: 'TRANSACTION_STATE_UNKNOWN'` (a transaction tab whose server
   * transaction may have been rolled back — MySQL after a lock wait timeout
   * whose status check failed): what the tab shows after the statement's own
   * error. The tab's transaction goes to `error` — Commit off, Rollback only.
   */
  transactionStateMessage?: string;
  /**
   * If true, this error is specific to the database (e.g., database offline, no permission)
   * and does NOT indicate the connection to the server is broken.
   * UI should NOT mark the connection as disconnected for database errors.
   */
  isDatabaseError?: boolean;
  /**
   * SQL Server-style numeric error code (the `number` field on mssql `RequestError`).
   * Populated when the underlying error is a SQL Server error with a known number
   * (e.g. 4060 = login cannot open DB; 916 = no permission; 18456 = login failed).
   */
  sqlErrorCode?: number;
  /**
   * SQL Server `class` field (severity level, 1-25) when available.
   */
  sqlErrorClass?: number;
  /**
   * Original (un-decorated) server message, before any UI-side prefixing or annotation.
   * Useful for power users / DBAs reading the raw cause.
   */
  originalMessage?: string;
  /**
   * True when the error is a request timeout (mssql driver `code === 'ETIMEOUT'`,
   * or equivalent for other engines). Surfaced separately from `sqlErrorCode` because
   * timeouts have no SQL-level error number.
   */
  timedOut?: boolean;
  /**
   * Structured location of the SQL error within the source document.
   * Populated by the engine adapter from the driver error string via the
   * sql-grammar `parse*Error` + `mapError` pipeline. When present the renderer
   * can place a squiggle/marker at the exact statement (and column) that failed.
   * Absent when the engine doesn't carry location info (e.g. plain ORA errors).
   */
  errorLocation?: {
    /** Zero-indexed document-absolute line of the error token. */
    line: number;
    /** Zero-indexed document-absolute column of the error token. */
    column: number;
    /** Stable batch ID from DocumentStructure (matches mapResult / mapError output). */
    batchId: string;
    /** Stable statement ID within the batch; null when the error can't be attributed to a statement. */
    statementId: string | null;
  };
};

/**
 * Request to cancel a query
 */
export type QueryCancelRequest = {
  /** Execution ID to cancel */
  executionId: string;
  /**
   * Explicit user escalation (Stop pressed again while "Stopping…"): stop
   * WAITING for the server to honour the cancel, and return the query now.
   * Never set by automatic cancels.
   *
   * What it does NOT do (measured, `Docs/OracleConnectionLease.md` §1): end the
   * database session. Oracle turns this into an immediate watchdog fire, which
   * destroys the CLIENT socket — the query returns as cancelled at once, and the
   * SERVER session stays in its wait until that wait ends on its own (~20 s for
   * a `DBMS_SESSION.SLEEP(20)`, or when a lock frees); a statement Oracle
   * already accepted and auto-committed then COMMITS, and a dropped connection
   * cannot retract it. The other adapters accept and ignore the flag, so the
   * escalation is harmless everywhere and the UI never branches per engine.
   */
  force?: boolean;
};

/**
 * Result of a cancel request
 */
export type QueryCancelResult = {
  /** Whether the cancel request was accepted */
  success: boolean;
  /** Error if cancel failed */
  error?: string;
  /**
   * `false` when the adapter had never heard of this execution id — it has
   * already finished, or it has not reached the adapter yet (the MSSQL
   * auto-connect window). `success` is then only "nothing to do": no engine
   * stopped anything, and a caller that knows a second adapter may hold the
   * execution should ask it. Absent means the adapter knew the execution.
   */
  known?: boolean;
};

/**
 * Progress callbacks for streaming results (optional, adapters may ignore)
 */
export type QueryExecutionCallbacks = {
  /** Called during execution phases */
  onProgress?: (progress: QueryExecutionProgress) => void;
  /** Called when a result set is complete (for streaming) */
  onResultSet?: (resultSet: QueryResultSet, index: number) => void;
  /**
   * Called with each batch of fetched rows for incremental population.
   * When provided and used by the adapter, result sets are returned with empty rows.
   * The caller is responsible for creating/finalizing ResultSources.
   */
  onRowBatch?: (params: {
    resultSetIndex: number;
    columns: QueryColumn[];
    rows: Record<string, unknown>[];
    totalRowCount: number;
  }) => void;
  /**
   * Called once per GO-separated batch as it begins execution (MSSQL SSTS adapter).
   * Allows the UI to show per-batch progress for long multi-batch scripts.
   */
  onBatchProgress?: (info: { batchIndex: number; totalBatches: number; elapsedMs: number }) => void;
};

/**
 * Progress information during query execution
 */
export type QueryExecutionProgress = {
  /** Current phase */
  phase: 'executing' | 'fetching';
  /** Rows fetched so far (during fetching phase) */
  fetchedRows?: number;
  /** Total rows to fetch (if known) */
  totalRows?: number;
};

/**
 * Utility to create a unique execution ID
 */
export function createExecutionId(tabId: string, runNumber?: number): string {
  const timestamp = Date.now();
  const suffix = runNumber !== undefined ? `-${runNumber}` : '';
  return `exec-${tabId}${suffix}-${timestamp}`;
}

// ---------------------------------------------------------------------------
// Result Cache IPC types (shared between Electron and renderer)
// ---------------------------------------------------------------------------

/** Request a window of rows from a cached result set */
export type ResultCacheWindowRequest = {
  uri: string;
  startIndex: number;
  count: number;
  /** Sort/filter generation WITHIN one source (see `ResultSource.generation`). */
  expectedGeneration: number;
  /**
   * Instance epoch of the source this read is FOR, as handed out when it was
   * created. Optional: omit it and any source registered at `uri` answers.
   *
   * Cache URIs are deterministic per tab and result-set index, so a source can
   * be disposed and replaced at the same URI by the tab's next execution while
   * an earlier read is still on its way. `expectedGeneration` cannot catch that
   * — every new source starts at generation 0 — so a reader that must not be
   * served a REPLACEMENT (Table Explorer reads a whole page back by URI) passes
   * the epoch instead, and a mismatch is answered like a disposed source.
   */
  expectedSourceEpoch?: number;
};

/** Response from result-cache:get-window */
export type ResultCacheWindowResponse = {
  rows: Record<string, unknown>[];
  columns: QueryColumn[];
  totalRowCount: number;
  /** Source generation at the time this response was produced (for staleness detection) */
  generation: number;
};

/** Request metadata for a cached result set */
export type ResultCacheMetadataRequest = {
  uri: string;
};

/** Capability flags surfaced from a result source to the renderer */
export type ResultCacheCapabilities = {
  canSort: boolean;
  canFilter: boolean;
  canEdit: boolean;
  supportsAccurateCount: boolean;
};

/** Response from result-cache:get-metadata */
export type ResultCacheMetadataResponse = {
  totalRowCount: number;
  columns: QueryColumn[];
  /** True if the result was truncated during execution (more rows exist on the server) */
  truncated: boolean;
  capabilities: ResultCacheCapabilities;
  /** Current source lifecycle state */
  state?: 'creating' | 'populating' | 'ready' | 'sorting' | 'filtering' | 'exporting' | 'cancelled' | 'failed' | 'disposed';
  /** Error message when state is 'failed' */
  errorMessage?: string;
};

/** Request to discard a cached result set (free memory) */
export type ResultCacheDiscardRequest = {
  uri: string;
};

/** Request to sort a cached result set */
export type ResultCacheSortRequest = {
  uri: string;
  columnId: string;
  direction: 'asc' | 'desc';
  expectedGeneration: number;
};

/** Response from result-cache:sort */
export type ResultCacheSortResponse = {
  /** Total row count after filtering (unchanged by sort-only) */
  totalRowCount: number;
  /** Source generation after sort (for staleness detection) */
  generation: number;
  /** When true, the source is not yet ready for sort/filter (e.g., still populating) */
  notReady?: boolean;
  /** Human-readable explanation when notReady is true */
  message?: string;
};

/** Request to filter a cached result set */
export type ResultCacheFilterRequest = {
  uri: string;
  columnFilters: Record<string, string>;
  globalFilter: string;
  columnKeys: string[];
  expectedGeneration: number;
};

/** Response from result-cache:filter */
export type ResultCacheFilterResponse = {
  /** Total row count after filtering */
  totalRowCount: number;
  /** Source generation after filter (for staleness detection) */
  generation: number;
  /** When true, the source is not yet ready for sort/filter (e.g., still populating) */
  notReady?: boolean;
  /** Human-readable explanation when notReady is true */
  message?: string;
};

/** Request to clear only sort (keep filter) */
export type ResultCacheClearSortRequest = {
  uri: string;
  expectedGeneration: number;
};

/** Response from result-cache:clear-sort */
export type ResultCacheClearSortResponse = {
  totalRowCount: number;
  /** Source generation after clear-sort (for staleness detection) */
  generation: number;
};

/** Request to clear only filter (keep sort) */
export type ResultCacheClearFilterRequest = {
  uri: string;
  expectedGeneration: number;
};

/** Response from result-cache:clear-filter */
export type ResultCacheClearFilterResponse = {
  totalRowCount: number;
  /** Source generation after clear-filter (for staleness detection) */
  generation: number;
};

/** Request to get a single full (untruncated) cell value from a result source */
export type ResultCacheGetCellValueRequest = {
  uri: string;
  rowIndex: number;
  columnId: string;
  expectedGeneration: number;
};

/** Response from result-cache:get-cell-value */
export type ResultCacheGetCellValueResponse = {
  value: unknown;
  generation: number;
  /** Error message when cell fetch failed (STS bridge errors) */
  error?: string;
  /** Typed STS error code when cell fetch failed */
  errorCode?: string;
};

/**
 * Request to batch-fetch full (untruncated) values for a set of columns
 * across a row range. Used by windowed map/peek surfaces (spatial T11 peek,
 * T13 QE Map view) that would otherwise decode truncated preview values.
 */
export type ResultCacheColumnValuesRequest = {
  uri: string;
  columnIds: string[];
  startIndex: number;
  count: number;
  expectedGeneration: number;
  /** Byte budget for the whole batch — response stops early when exceeded. Default: 64 MB. */
  maxTotalBytes?: number;
};

/** Response from result-cache:get-column-values */
export type ResultCacheColumnValuesResponse = {
  /** One record per row, containing only the requested columnIds, with FULL (untruncated) values */
  values: Record<string, unknown>[];
  /** Number of rows actually returned — may be < count when maxTotalBytes was exceeded */
  rowsReturned: number;
  /** True when the byte budget was exceeded before all requested rows could be fetched */
  byteBudgetExceeded: boolean;
  generation: number;
};

/** Request to stream-export a cached result set to CSV */
export type ResultCacheExportCsvRequest = {
  uri: string;
  /** Optional file path — if omitted, Electron shows a save dialog */
  filePath?: string;
  /**
   * T5 — WKT export parity: column ids (matching `column.id ?? column.name`)
   * whose values are native-spatial. Requires `engine` to take effect.
   */
  spatialColumnIds?: string[];
  /** T5 — engine id used to decode `spatialColumnIds` to WKT. */
  engine?: string;
  /**
   * Resume a previously-partial export: append to `filePath` (required in
   * this mode — no save dialog) starting at this 0-based row index. The
   * existing file is kept on any outcome, including cancel and failure.
   */
  resumeFromRow?: number;
};

/**
 * Which pipeline ultimately finished (or failed) a CSV export:
 * - 'sts-save-csv'   — STS-native `query/saveCsv` (server writes the file,
 *                      no row data crosses JSON-RPC; MSSQL only)
 * - 'subset-stream'  — windowed `query/subset` fetches streamed client-side
 * An export that started on the native path but fell back mid-way reports
 * 'subset-stream' — the value describes what the export ended on.
 */
export type CsvExportPath = 'sts-save-csv' | 'subset-stream';

/**
 * Why rows were skipped during a CSV export, classified from the terminal
 * per-row error: 'transient' = timeouts (the expected fat-row/congestion
 * class), 'unknown' = an error class we did NOT anticipate — the bug-smell
 * bucket telemetry exists to surface — 'mixed' = both occurred.
 */
export type SkipCauseClass = 'transient' | 'unknown' | 'mixed';

/** Response from result-cache:export-csv */
export type ResultCacheExportCsvResponse =
  | {
      success: true;
      filePath: string;
      rowCount: number;
      exportPath?: CsvExportPath;
      /**
       * Absolute 0-based indexes of unreadable rows the subset path skipped
       * (ascending). Set only when rows were skipped; the native path never
       * skips rows. `rowCount` counts skipped positions too (cursor
       * semantics), so data rows = rowCount − skippedRows.length.
       */
      skippedRows?: number[];
      /** Classified cause of the skips — present only when rows were skipped. */
      skipCauseClass?: SkipCauseClass;
    }
  | {
      success: false;
      error: string;
      cancelled?: boolean;
      /**
       * When a non-cancelled export failed mid-stream, the rows written so
       * far are kept on disk instead of being deleted — these fields point
       * at that partial file so the UI can offer it to the user.
       */
      partialFilePath?: string;
      partialRowCount?: number;
      exportPath?: CsvExportPath;
      /** Skipped-row indexes for a partial-kept failure (best effort). */
      skippedRows?: number[];
      /** Classified cause of the skips — present only when rows were skipped. */
      skipCauseClass?: SkipCauseClass;
    };

/** Progress event pushed from main→renderer during CSV export */
export type ResultCacheExportCsvProgress = {
  uri: string;
  phase: 'exporting';
  current: number;
  total: number;
  /** Present while a stalled batch fetch is being retried (export keeps going). */
  retry?: { attempt: number; maxAttempts: number };
  /**
   * Present while a stalled batch is being bisected into smaller fetches —
   * `batchSize` is the current sub-batch size (export keeps going).
   */
  recovery?: { batchSize: number };
  /** Present once unreadable rows were skipped — cumulative count (export keeps going). */
  skipped?: { count: number };
};

/** Progress event pushed from main→renderer during result source population */
export type ResultSourceProgressEvent = {
  uri: string;
  loadedRowCount: number;
  finalRowCount: number | undefined;
  state: 'populating' | 'ready' | 'cancelled' | 'failed';
  generation: number;
  /** Columns — included on first event per result set so the renderer can show headers */
  columns?: QueryColumn[];
  /**
   * Delivery mode for this result set:
   *  - `'inline'`: rows are being shipped over IPC in `rowsBatch` and accumulated by the renderer.
   *  - `'windowed'`: rows live in a backend ResultSource; renderer must use `result-cache:get-window`.
   *
   * A result set can transition from `'inline'` to `'windowed'` when the pending buffer in main
   * crosses the fast-path threshold. On that transition the renderer must discard its inline
   * accumulator and switch to the windowed path.
   */
  mode?: 'inline' | 'windowed';
  /**
   * Incremental batch of rows for this event (only present when `mode === 'inline'`).
   * The renderer appends these to its per-result-set accumulator.
   */
  rowsBatch?: Record<string, unknown>[];
};
